import {
  type Address,
  type Client,
  CreateClientSchema,
  type CreateClientInput,
  type PersonType,
  displayPhone,
  formatDocument,
  formatPhone,
} from '@pm/schemas';
import { Button, Dialog, DialogContent, DialogFooter, Field, Input, Segmented } from '@pm/ui';
import { useState } from 'react';
import { api } from '@/lib/api';
import { keys, useInvalidating } from '@/lib/queries';
import { useToast } from '@/lib/toast';

type Form = {
  person_type: PersonType;
  legal_name: string;
  trade_name: string;
  document: string;
  state_registration: string;
  municipal_registration: string;
  email: string;
  phone: string;
  website: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  zip: string;
};

const empty: Form = {
  person_type: 'COMPANY',
  legal_name: '',
  trade_name: '',
  document: '',
  state_registration: '',
  municipal_registration: '',
  email: '',
  phone: '',
  website: '',
  street: '',
  number: '',
  complement: '',
  district: '',
  city: '',
  state: '',
  zip: '',
};

function fromClient(c: Client): Form {
  const a: Partial<Address> = c.address ?? {};
  return {
    person_type: c.person_type,
    legal_name: c.legal_name,
    trade_name: c.trade_name ?? '',
    document: c.document ? formatDocument(c.document, c.person_type) : '',
    state_registration: c.state_registration ?? '',
    municipal_registration: c.municipal_registration ?? '',
    email: c.email ?? '',
    phone: displayPhone(c.phone),
    website: c.website ?? '',
    street: a.street ?? '',
    number: a.number ?? '',
    complement: a.complement ?? '',
    district: a.district ?? '',
    city: a.city ?? '',
    state: a.state ?? '',
    zip: a.zip ?? '',
  };
}

/** SPEC §6 — cadastro PF/PJ. */
export function ClientDialog({
  open,
  onOpenChange,
  client,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  client?: Client;
  onSaved?: (c: Client) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={client ? 'Editar cliente' : 'Novo cliente'} size="lg">
        <ClientForm client={client} onOpenChange={onOpenChange} onSaved={onSaved} />
      </DialogContent>
    </Dialog>
  );
}

function ClientForm({
  client,
  onOpenChange,
  onSaved,
}: {
  client?: Client;
  onOpenChange: (o: boolean) => void;
  onSaved?: (c: Client) => void;
}) {
  const toast = useToast();
  const [form, setForm] = useState<Form>(client ? fromClient(client) : empty);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const save = useInvalidating(
    (input: CreateClientInput) =>
      client
        ? api.patch<Client>(`/clients/${client.id}`, input)
        : api.post<Client>('/clients', input),
    [['clients'], ['contexts'], client ? keys.client(client.id) : ['client']],
  );
  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.value });
  const pj = form.person_type === 'COMPANY';

  function submit() {
    const address = {
      street: form.street,
      number: form.number,
      complement: form.complement,
      district: form.district,
      city: form.city,
      state: form.state,
      zip: form.zip,
    };
    const hasAddress = Object.values(address).some(Boolean);
    const parsed = CreateClientSchema.safeParse({
      person_type: form.person_type,
      legal_name: form.legal_name,
      trade_name: pj ? form.trade_name || undefined : undefined,
      document: form.document || undefined,
      state_registration: pj ? form.state_registration || undefined : undefined,
      municipal_registration: pj ? form.municipal_registration || undefined : undefined,
      email: form.email,
      phone: form.phone || undefined,
      website: form.website || undefined,
      address: hasAddress
        ? Object.fromEntries(Object.entries(address).filter(([, v]) => v))
        : undefined,
    });
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    save.mutate(parsed.data, {
      onSuccess: (c) => {
        toast.success(client ? 'Cliente atualizado' : 'Cliente criado', c.legal_name);
        onOpenChange(false);
        onSaved?.(c);
      },
      onError: (e) => toast.error(e),
    });
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={(e) => (e.preventDefault(), submit())}>
      <Segmented
        ariaLabel="Tipo"
        value={form.person_type}
        onChange={(v) =>
          setForm({
            ...form,
            person_type: v,
            document: form.document ? formatDocument(form.document, v) : '',
          })
        }
        options={[
          { value: 'COMPANY', label: 'Pessoa jurídica' },
          { value: 'INDIVIDUAL', label: 'Pessoa física' },
        ]}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={pj ? 'Razão social' : 'Nome'} htmlFor="c-legal" error={errors.legal_name}>
          <Input
            id="c-legal"
            autoFocus
            value={form.legal_name}
            onChange={set('legal_name')}
            invalid={Boolean(errors.legal_name)}
          />
        </Field>
        {pj ? (
          <Field label="Nome fantasia" htmlFor="c-trade">
            <Input id="c-trade" value={form.trade_name} onChange={set('trade_name')} />
          </Field>
        ) : null}
        <Field label={pj ? 'CNPJ' : 'CPF'} htmlFor="c-doc" error={errors.document}>
          <Input
            id="c-doc"
            mono
            value={form.document}
            onChange={(e) =>
              setForm({ ...form, document: formatDocument(e.target.value, form.person_type) })
            }
            maxLength={pj ? 18 : 14}
            placeholder={pj ? '00.000.000/0000-00' : '000.000.000-00'}
            invalid={Boolean(errors.document)}
          />
        </Field>
        {pj ? (
          <>
            <Field label="Inscrição estadual" htmlFor="c-ie">
              <Input
                id="c-ie"
                mono
                value={form.state_registration}
                onChange={set('state_registration')}
              />
            </Field>
            <Field label="Inscrição municipal" htmlFor="c-im">
              <Input
                id="c-im"
                mono
                value={form.municipal_registration}
                onChange={set('municipal_registration')}
              />
            </Field>
          </>
        ) : null}
        <Field label="E-mail" htmlFor="c-email" error={errors.email}>
          <Input
            id="c-email"
            type="email"
            value={form.email}
            onChange={set('email')}
            invalid={Boolean(errors.email)}
          />
        </Field>
        <Field label="Telefone" htmlFor="c-phone">
          <Input
            id="c-phone"
            type="tel"
            mono
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
            placeholder="(11) 98765-4321"
            maxLength={19}
          />
        </Field>
        <Field label="Site" htmlFor="c-site">
          <Input
            id="c-site"
            mono
            value={form.website}
            onChange={set('website')}
            placeholder="https://"
          />
        </Field>
      </div>
      <div>
        <p className="label-caps mb-2">Endereço</p>
        <div className="grid gap-3 sm:grid-cols-6">
          <Field label="CEP" htmlFor="c-zip">
            <Input id="c-zip" mono value={form.zip} onChange={set('zip')} />
          </Field>
          <div className="sm:col-span-3">
            <Field label="Logradouro" htmlFor="c-street">
              <Input id="c-street" value={form.street} onChange={set('street')} />
            </Field>
          </div>
          <Field label="Número" htmlFor="c-num">
            <Input id="c-num" mono value={form.number} onChange={set('number')} />
          </Field>
          <Field label="Compl." htmlFor="c-comp">
            <Input id="c-comp" value={form.complement} onChange={set('complement')} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Bairro" htmlFor="c-dist">
              <Input id="c-dist" value={form.district} onChange={set('district')} />
            </Field>
          </div>
          <div className="sm:col-span-3">
            <Field label="Cidade" htmlFor="c-city">
              <Input id="c-city" value={form.city} onChange={set('city')} />
            </Field>
          </div>
          <Field label="UF" htmlFor="c-uf">
            <Input id="c-uf" mono maxLength={2} value={form.state} onChange={set('state')} />
          </Field>
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
          Cancelar
        </Button>
        <Button variant="primary" type="submit" loading={save.isPending}>
          {client ? 'Salvar' : 'Criar cliente'}
        </Button>
      </DialogFooter>
    </form>
  );
}
